import { Api } from '../api/root-api';
import {
  DocumentRequest,
  Workspace,
  WorkspacePostRequest,
  DocumentResponse,
  CreatePrincipalWorkspaceRequest,
  SignedUrlRequest,
  SignedUrlResponse,
  CreatePrincipalWorkspaceResponse,
  BillingAndPlanDetailsRequest,
  BillingAndPlanDetailsResponse,
  DataFlushResponse,
  DataFlushRequest,
  FlushChannelDataRequest,
  FlushChannelDataResponse,
  OrgsDocumentRequest,
  UpdateChatWidgetSettingsResponse,
  UpdateChatWidgetSettingsRequest,
  GetOrgsDocumentRequest,
  GetOrgsDocumentResponse,
} from './model';
import { addWorkspace } from './slice';

export const workspaceApi = Api.injectEndpoints({
  endpoints: (builder) => ({
    getWorkspace: builder.query<Workspace[], void>({
      query() {
        return {
          url: `workspaces?includeBalance=true&includeWhatsAppConfigs=true`,
          method: 'GET',
        };
      },
      providesTags: ['workspace'],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(addWorkspace(data));
        } catch (err) {
          console.log(err, 'dispatchErr');
        }
      },
    }),
    createWorkspace: builder.mutation<Workspace, WorkspacePostRequest>({
      query(arg) {
        return { url: `workspaces`, method: 'POST', body: arg };
      },
    }),
    updateWorkspaceDetails: builder.mutation<any, any>({
      query(arg) {
        return {
          url: 'workspace/details',
          method: 'PUT',
          body: arg,
        };
      },
      invalidatesTags: ['workspace'],
    }),
    updateWorkspaceCredentials: builder.mutation<any, any>({
      query(arg) {
        return {
          url: 'workspace-details/credentials',
          method: 'POST',
          body: arg,
        };
      },
      invalidatesTags: ['workspace'],
    }),
    uploadWorkspaceDocuments: builder.mutation<
      { presignedUrl: string },
      DocumentRequest
    >({
      query(arg) {
        const { workspaceId, ...body } = arg;
        return {
          url: `workspaces/documents/${workspaceId}`,
          method: 'POST',
          body: body,
        };
      },
    }),

    uploadOrgsWorkspaceDocuments: builder.mutation<
      {
        presignedUrl: {
          presignedUrl: string;
        };
      },
      OrgsDocumentRequest
    >({
      query(arg) {
        const { sellerWorkspaceId, body } = arg;
        return {
          url: `hub/orgs/api/docs/presignedurl/${sellerWorkspaceId}`,
          method: 'POST',
          params: { sellerWorkspaceId },
          body: body,
        };
      },
    }),
    getWorkspaceDocument: builder.query<
      GetOrgsDocumentResponse,
      GetOrgsDocumentRequest
    >({
      query(arg) {
        return {
          url: `/hub/orgs/api/docs/zo-file/file-operations/${arg.params.sellerWorkspaceId}`,
          method: 'POST',
          params: arg.params,
          body: arg.body,
        };
      },
    }),
    qrCodeWorkspaceDocuments: builder.mutation<
      { presignedUrl: string },
      DocumentRequest
    >({
      query(arg) {
        const { workspaceId, ...body } = arg;
        return {
          url: `workspaces/documents/v3/${workspaceId}`,
          method: 'POST',
          body: body,
        };
      },
    }),
    getDocuments: builder.query<DocumentResponse[], DocumentRequest>({
      query(arg) {
        const { workspaceId, ...params } = arg;
        return {
          url: `hub/orgs/api/workspace/documents`,
          method: 'GET',
          params: { ...params, sellerWorkspaceId: workspaceId },
        };
      },
    }),
    getSignedUrlList: builder.query<SignedUrlResponse[], SignedUrlRequest>({
      query(arg) {
        return {
          url: `signUrl/list`,
          method: 'GET',
          params: arg,
        };
      },
    }),
    deleteDocument: builder.query<DocumentResponse[], DocumentRequest>({
      query(arg) {
        const { workspaceId, ...params } = arg;
        return {
          url: `workspaces/documents/delete/${workspaceId}`,
          method: 'POST',
          params: params,
        };
      },
    }),
    createPrincipalWorkspace: builder.mutation<
      CreatePrincipalWorkspaceResponse,
      CreatePrincipalWorkspaceRequest
    >({
      query(arg) {
        return {
          url: 'admin/principal/workspace',
          method: 'POST',
          body: arg,
        };
      },
    }),
    getBillingAndPlanDetails: builder.query<
      BillingAndPlanDetailsResponse,
      BillingAndPlanDetailsRequest
    >({
      query(arg) {
        return {
          url: `/hub/rewards-vault/api/billing/planDetails/${arg.sellerWorkspaceId}`,
          method: 'GET',
          params: { sellerWorkspaceId: arg.sellerWorkspaceId },
        };
      },
    }),
    dataFlush: builder.query<DataFlushResponse, DataFlushRequest>({
      query(args) {
        return {
          url: `/hub/orgs/api/data-flush`,
          method: 'DELETE',
          body: args.body,
          params: args.params,
        };
      },
    }),
    workspaceDelete: builder.query<DataFlushResponse, DataFlushRequest>({
      query(args) {
        return {
          url: `admin/data-flush`,
          method: 'DELETE',
          body: args.body,
          params: args.params,
        };
      },
    }),
    flushChannelData: builder.mutation<
      FlushChannelDataResponse,
      FlushChannelDataRequest
    >({
      query(args) {
        return {
          url: `hub/communication-v2/api//threads/purge-channel-data`,
          method: 'POST',
          body: args.body,
          params: args.params,
        };
      },
    }),
    updateChatWidgetSettings: builder.mutation<
      UpdateChatWidgetSettingsResponse,
      UpdateChatWidgetSettingsRequest
    >({
      query(args) {
        return {
          url: `hub/orgs/api/workspace/chat-widget-settings`,
          method: 'POST',
          body: args.body,
          params: args.params,
        };
      },
    }),
  }),
});

export const {
  useLazyGetWorkspaceQuery,
  useCreateWorkspaceMutation,
  useUpdateWorkspaceCredentialsMutation,
  useUpdateWorkspaceDetailsMutation,
  useUploadWorkspaceDocumentsMutation,
  useQrCodeWorkspaceDocumentsMutation,
  useLazyGetDocumentsQuery,
  useGetDocumentsQuery,
  useLazyDeleteDocumentQuery,
  useCreatePrincipalWorkspaceMutation,
  useLazyGetSignedUrlListQuery,
  useLazyGetBillingAndPlanDetailsQuery,
  useLazyDataFlushQuery,
  useLazyWorkspaceDeleteQuery,
  useFlushChannelDataMutation,
  useUploadOrgsWorkspaceDocumentsMutation,
  useUpdateChatWidgetSettingsMutation,
  useLazyGetWorkspaceDocumentQuery,
} = workspaceApi;
